import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Field, SubmitButton } from "@/components/site/FormBits";

const HERO = "https://images.unsplash.com/photo-1506501139174-099022df5260?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function AuthPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const next = params.get("next");

  useEffect(() => {
    if (user) nav(next || (user.role === "admin" ? "/admin" : "/portal"), { replace: true });
  }, [user, next, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ ...form, phone: form.phone || null });
      toast.success(mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const tab = (m, label) => (
    <button type="button" data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
      className={`pb-3 text-sm border-b-2 transition-colors ${mode === m ? "border-[#9F7E44] text-[#121417]" : "border-transparent text-[#847F77] hover:text-[#121417]"}`}>
      {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid lg:grid-cols-2 gap-14 items-center" data-testid="auth-page">
      <div className="hidden lg:block relative aspect-[4/5] overflow-hidden grain">
        <img src={HERO} alt="London" className="h-full w-full object-cover" />
      </div>
      <div className="max-w-md w-full">
        <p className="eyebrow">Client account</p>
        <h1 className="mt-4 font-display text-4xl tracking-tight">{mode === "login" ? "Sign in" : "Create your account"}</h1>
        <div className="mt-8 flex gap-8 border-b border-[#E4E1D9]">{tab("login", "Sign in")}{tab("register", "Create account")}</div>
        <form onSubmit={submit} className="mt-8 space-y-5">
          {mode === "register" && (
            <Field label="Full name" required>
              <input data-testid="auth-input-fullname" required value={form.full_name} onChange={set("full_name")} className="field-input" />
            </Field>
          )}
          <Field label="Email" required>
            <input data-testid="auth-input-email" type="email" required value={form.email} onChange={set("email")} className="field-input" />
          </Field>
          {mode === "register" && (
            <Field label="Contact number" hint="Optional">
              <input data-testid="auth-input-phone" type="tel" value={form.phone} onChange={set("phone")} className="field-input" placeholder="07…" />
            </Field>
          )}
          <Field label="Password" required hint={mode === "register" ? "At least 8 characters" : undefined}>
            <input data-testid="auth-input-password" type="password" required minLength={mode === "register" ? 8 : undefined}
              value={form.password} onChange={set("password")} className="field-input" />
          </Field>
          {mode === "login" && (
            <Link to="/forgot-password" data-testid="auth-forgot-link" className="block -mt-2 text-sm text-[#9F7E44] hover:text-[#866835]">Forgot password?</Link>
          )}
          {error && <p data-testid="auth-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{error}</p>}
          <SubmitButton loading={loading} testId="auth-submit-btn" className="w-full">
            {mode === "login" ? "Sign in" : "Create account"}
          </SubmitButton>
        </form>
      </div>
    </div>
  );
}
